import { useState, useEffect, useRef, useCallback } from 'react'
import { DemandesState } from './DemandeAbsence.types'

import { useSessionStore } from '../../lib/Store/sessionStore.component'
import { DemandeAbsenceType } from '../../api/DemandeAbsence/types'
import { DemandeAbsenceModeNavigation } from './DemandeAbsence.types'
import { NotifyType } from '../../components/Notification/Notification.types'
import { NouvelleDemandeModeAbsenceParam } from '../NouvelleDemande/NouvelleDemande.types'
import { DemandeAbsenceApi } from '../../api/DemandeAbsence'
import { usePolling } from '../../hooks/usePolling/usePolling.hook'
import { Dayjs } from 'dayjs'

// Actions communes sur une demande d'absence (sélection, annulation, validation),
// sans chargement de liste ni polling. À utiliser partout où l'on a juste besoin
// d'afficher/gérer le détail d'une demande (ex: CalendrierUniversel) sans dupliquer
// les appels réseau de la liste des demandes.
export const useDemandeAbsenceActions = (
  navigation: DemandeAbsenceModeNavigation,
  notify?: NotifyType,
  onDemandeAnnulee?: (identifiant: number) => void,
) => {
  const [selectedDemande, setSelectedDemande] = useState<DemandeAbsenceType | null>(null)

  const handleVisualiseDemande = (demande: DemandeAbsenceType) => {
    setSelectedDemande(demande)
  }

  const closeDemandeDetail = () => {
    setSelectedDemande(null)
  }

  const handleAnnule = (identifiant: number) => async () => {
    try {
      await DemandeAbsenceApi.deleteDemandeAbsence(identifiant, notify)
      closeDemandeDetail();
      onDemandeAnnulee?.(identifiant)
    } catch (error) {
      notify?.error('Erreur lors de la suppression de la demande:' + error, 2000)
    }
  }

  const handleValide = (demande: DemandeAbsenceType) => async () => {
    // Fermer la modal d'abord
    closeDemandeDetail();

    // Convertir DemandeType vers NouvelleDemandeModeAbsenceParam
    const absenceParam: NouvelleDemandeModeAbsenceParam = {
      identifiant: demande.identifiant || 0,
      dateDebut: demande.dateDebut || '',
      dateFin: demande.dateFin || '',
      heureDebut: demande.heureDebut || 0,
      heureFin: demande.heureFin || 0,
      observations: demande.observations || '',
      prevision: demande.prevision === 1,
      statut: demande.statut,
      valide: demande.valide,
      natureAbsence: {
        identifiant: demande.natureAbsence?.identifiant || 0,
        libelle: demande.natureAbsence?.libelle || '',
        code: demande.natureAbsence?.code || '',
        ordre: 0
      }
    };
    navigation.navigate('NouvelleDemande', { absence: absenceParam });
  }

  return {
    selectedDemande,
    setSelectedDemande,
    handleVisualiseDemande,
    closeDemandeDetail,
    handleAnnule,
    handleValide,
  }
}

export const useDemandeAbsenceState = (
  navigation: DemandeAbsenceModeNavigation,
  notify?: NotifyType,
  dateDebutRef?: Dayjs,
  dateFinRef?: Dayjs,
) => {
  const {currentLogin} = useSessionStore.getState().getCurrentInfos();

  // Référence stable pour notify pour éviter les re-rendus
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  // États principaux
  const [demandes, setDemandes] = useState<DemandesState>({
    enCours: [],
    acceptees: [],
    refusees: [],
    annulees: [],
  })

  const [loading, setLoading] = useState(true)
  const [showAnimation, setShowAnimation] = useState(false)

  // TODO : Regarder pour potentiellement retirer
  const checkSpaceForAnimation = (event: any) => {
    const { height } = event.nativeEvent.layout
    const animationHeight = 450
    setShowAnimation(height > animationHeight)
  }

  const buildComplement = useCallback(() => `identifiantAgent=${currentLogin?.identifiant}`
    .concat(`&withNatureAbsence=true`)
    .concat(`&withDoc=true`)
    .concat(`&dateRefDebut=${dateDebutRef?.format('YYYY-MM-DD')}`)
    .concat(`&dateRefFin=${dateFinRef?.format('YYYY-MM-DD')}`)
    .concat(`&duree=1`), [currentLogin?.identifiant, dateDebutRef, dateFinRef])

  const {
    selectedDemande,
    setSelectedDemande,
    handleVisualiseDemande,
    closeDemandeDetail,
    handleAnnule,
    handleValide,
  } = useDemandeAbsenceActions(navigation, notify, () => loadDemandes(true))

  // Chargement des données (réutilisable pour le polling)
  const loadDemandes = useCallback(async (silent = false) => {
    if (!currentLogin?.identifiant || !dateDebutRef || !dateFinRef) return
    if (!silent) setLoading(true)

    try {
      const complement = buildComplement()

      const apiData: DemandeAbsenceType[] = await DemandeAbsenceApi.loadDemandesAbsence(complement, silent ? undefined : notifyRef.current, silent)
      const safeApiData: DemandeAbsenceType[] = Array.isArray(apiData) ? apiData : []

      const enCours = safeApiData.filter((demande) => demande.etat === 'enCours')
      const acceptees = safeApiData.filter((demande) => demande.etat === 'acceptees')
      const refusees = safeApiData.filter((demande) => demande.etat === 'refusees')
      const annulees = safeApiData.filter((demande) => demande.etat === 'annulees')
      setDemandes({ enCours, acceptees, refusees, annulees })

      // Mettre à jour selectedDemande si la modal est ouverte
      setSelectedDemande(prev => {
        if (!prev?.identifiant) return prev
        const updated = safeApiData.find(d => d.identifiant === prev.identifiant)
        return updated || null // null si supprimée côté serveur
      })
    } catch (error) {
      if (!silent) console.error('❌ Error loading data:', error)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [dateDebutRef, dateFinRef, currentLogin?.identifiant, buildComplement, setSelectedDemande])

  // Chargement initial
  useEffect(() => {
    loadDemandes()
  }, [loadDemandes])

  // Polling silencieux toutes les 30s via hook générique
  usePolling<DemandeAbsenceType[]>({
    fetchFn: async () => {
      if (!currentLogin?.identifiant || !dateDebutRef || !dateFinRef) return null
      return DemandeAbsenceApi.loadDemandesAbsence(buildComplement(), undefined, true)
    },
    enabled: !!currentLogin?.identifiant && !!dateDebutRef && !!dateFinRef,
    hasChanged: (prev, next) => {
      const prevSafe = Array.isArray(prev) ? prev : []
      const nextSafe = Array.isArray(next) ? next : []
      if (prevSafe.length !== nextSafe.length) return true
      return prevSafe.some((d, i) =>
        d.identifiant !== nextSafe[i]?.identifiant ||
        d.statut !== nextSafe[i]?.statut ||
        d.valide !== nextSafe[i]?.valide ||
        d.observations !== nextSafe[i]?.observations
      )
    },
    onChanged: (next) => {
      const safeNext = Array.isArray(next) ? next : []
      const enCours = safeNext.filter((d) => d.etat === 'enCours')
      const acceptees = safeNext.filter((d) => d.etat === 'acceptees')
      const refusees = safeNext.filter((d) => d.etat === 'refusees')
      const annulees = safeNext.filter((d) => d.etat === 'annulees')
      setDemandes({ enCours, acceptees, refusees, annulees })

      // Mettre à jour selectedDemande si la modal est ouverte
      setSelectedDemande(prev => {
        if (!prev?.identifiant) return prev
        const updated = safeNext.find(d => d.identifiant === prev.identifiant)
        return updated || null
      })

      notifyRef.current?.info('Les demandes ont été mises à jour', 3000)
    },
  })

  return {
    // États
    demandes,
    loading,
    showAnimation,
    selectedDemande,

    // Actions
    handleVisualiseDemande,
    closeDemandeDetail,
    checkSpaceForAnimation,
    handleAnnule,
    handleValide,
  }
}
